import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useTrainingCourse from "../../Hooks/useTrainingCourse";
import SubmitLoader from "../../Components/SubmitLoader";
import AlertMessage from "../../Components/AlertMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEye, faBookOpen, faUsers, faCalendarAlt, faClock, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import noImageAvailable from "../../Assets/no-image-available.png";
import "./TrainingCourses.css";

const TrainingCourses = () => {
  const navigate = useNavigate();
  const {
    courses,
    userEnrollments,
    coursesLoading,
    enrollmentsLoading,
    coursesError,
    enrollmentsError,
    fetchCourses,
    fetchUserEnrollments,
    isEnrolledInCourse,
    getCourseEnrollment
  } = useTrainingCourse();

  // Utility function to get course cover image
  const getCourseCoverImage = (course) => {
    if (course.coverImageContent && course.coverImageExtension) {
      try {
        let base64String;

        // Handle different formats from .NET
        if (typeof course.coverImageContent === 'string') {
          // If it's already a base64 string, use it directly
          base64String = course.coverImageContent;
        } else if (course.coverImageContent instanceof Uint8Array) {
          // Convert Uint8Array to base64
          base64String = btoa(String.fromCharCode(...course.coverImageContent));
        } else if (Array.isArray(course.coverImageContent)) {
          // Convert regular array to base64
          base64String = btoa(String.fromCharCode(...new Uint8Array(course.coverImageContent)));
        } else {
          // console.warn('Unexpected coverImageContent format:', typeof course.coverImageContent);
          return { src: noImageAvailable, isPlaceholder: true };
        }

        // Handle extension format - remove leading dot if present
        const extension = course.coverImageExtension.startsWith('.') 
          ? course.coverImageExtension.substring(1) 
          : course.coverImageExtension;

        return { src: `data:image/${extension};base64,${base64String}`, isPlaceholder: false };
      } catch (error) {
        // console.error('Error converting cover image:', error);
        return { src: noImageAvailable, isPlaceholder: true };
      }
    }
    return { src: noImageAvailable, isPlaceholder: true };
  };

  const [isCompletedExpanded, setIsCompletedExpanded] = useState(false);

  useEffect(() => {
    fetchCourses();
    fetchUserEnrollments();
  }, []);

  // Get courses by category
  const getValidCourses = () => {
    if (!courses) return [];
    return courses.filter(course => course.publishStatus === 2 && course.activationStatus === 1);
  };

  const getEnrolledCourses = () => {
    const validCourses = getValidCourses();
    return validCourses.filter(course => {
      const isEnrolled = isEnrolledInCourse(course.courseId);
      const enrollment = getCourseEnrollment(course.courseId);
      const assessmentStatus = enrollment?.courseAssessmentStatus;
      return isEnrolled && assessmentStatus !== "P";
    });
  };

  const getAvailableCourses = () => {
    const validCourses = getValidCourses();
    return validCourses.filter(course => !isEnrolledInCourse(course.courseId));
  };

  const getCompletedCourses = () => {
    const validCourses = getValidCourses();
    return validCourses.filter(course => {
      const isEnrolled = isEnrolledInCourse(course.courseId);
      const enrollment = getCourseEnrollment(course.courseId);
      const assessmentStatus = enrollment?.courseAssessmentStatus;
      return isEnrolled && assessmentStatus === "P";
    });
  };

  const handleViewDetails = (courseId) => {
    navigate(`/trainingcourses/coursedetails/${courseId}`);
  };

  const getEnrollmentStatus = (courseId) => {
    const enrollment = getCourseEnrollment(courseId);
    if (!enrollment) return null;

    const status = enrollment.courseAssessmentStatus;
    switch (status) {
      case "P":
        return { text: "Passed", class: "status-completed", icon: faCheck };
      case "F":
        return { text: "Failed", class: "status-failed", icon: faCheck };
      case "I":
        return { text: "In Progress", class: "status-progress", icon: faBookOpen };
      case "N":
      case null:
      case undefined:
        return { text: "Enrolled", class: "status-enrolled", icon: faUsers };
      default:
        return { text: "Enrolled", class: "status-enrolled", icon: faUsers };
    }
  };

  const formatCourseDate = (dateString, fallback = "N/A") => {
    if (!dateString) return fallback;
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const enrolledCourses = getEnrolledCourses();
  const availableCourses = getAvailableCourses();
  const completedCourses = getCompletedCourses();

  const isLoading = coursesLoading || enrollmentsLoading;
  const hasError = coursesError || enrollmentsError;

  if (isLoading) {
    return <SubmitLoader />;
  }

  if (hasError) {
    return (
      <AlertMessage
        message={coursesError || enrollmentsError}
        type="error"
        className="training-courses-error"
      />
    );
  }

  return (
    <div className="training-courses-container">
      <div className="training-courses-header">
        <h1>Training Courses</h1>
        <p>Discover and enroll in available training courses to enhance your maritime skills.</p>
      </div>

      {/* Enrolled Courses */}
      <div className="course-section">
        <div className="section-header">
          <h2>
            <FontAwesomeIcon icon={faBookOpen} className="section-icon" />
            Enrolled Courses
            <span className="course-count">{enrolledCourses.length}</span>
          </h2>
        </div>
        <div className="courses-grid">
          {enrolledCourses.length === 0 ? (
            <div className="no-courses">
              <FontAwesomeIcon icon={faBookOpen} className="no-courses-icon" />
              <h3>No enrolled courses</h3>
              <p>You haven't enrolled in any courses yet.</p>
            </div>
          ) : (
            enrolledCourses.map((course) => {
              const enrollmentStatus = getEnrollmentStatus(course.courseId);
              const isFailed = enrollmentStatus?.class === 'status-failed';
              const coverImageData = getCourseCoverImage(course);

              return (
                <div key={course.courseId} className={`course-card enrolled ${isFailed ? 'failed' : ''}`}>
                  <div className="course-card-image">
                    <img 
                      src={coverImageData.src}
                      alt={course.courseName}
                      className="course-cover-image"
                      data-placeholder={coverImageData.isPlaceholder}
                      onError={(e) => {
                        e.target.src = noImageAvailable;
                        e.target.setAttribute('data-placeholder', 'true');
                      }}
                    />
                  </div>

                  <div className="course-card-header">
                    <div className="course-code-version">
                      <span className="course-code">{course.courseCode}</span>
                      {course.courseVersion && (
                        <span className="course-version">v{course.courseVersion}</span>
                      )}
                    </div>
                    {enrollmentStatus && (
                      <div className={`enrollment-badge ${enrollmentStatus.class}`}>
                        <FontAwesomeIcon icon={enrollmentStatus.icon} />
                        <span>{enrollmentStatus.text}</span>
                      </div>
                    )}
                  </div>

                  <div className="course-card-body">
                    <h3 className="course-title">{course.courseName}</h3>
                    <p className="course-description">
                      {course.courseDecription || "No description available"}
                    </p>

                    <div className="course-details">
                      <div className="course-detail">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <span>Started: {formatCourseDate(getCourseEnrollment(course.courseId)?.courseStartDate)}</span>
                      </div>
                      <div className="course-detail">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <span>Complete by: {formatCourseDate(getCourseEnrollment(course.courseId)?.courseCompleteByDate)}</span>
                      </div>
                      <div className="course-detail">
                        <FontAwesomeIcon icon={faClock} />
                        <span>{course.hoursToComplete} Hours to finish</span>
                      </div>
                      {course.dlTrainingCourseModules && (
                        <div className="course-detail">
                          <FontAwesomeIcon icon={faBookOpen} />
                          <span>{course.dlTrainingCourseModules.length} Module{course.dlTrainingCourseModules.length !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="course-card-footer">
                    <button
                      className="btn-view-details"
                      onClick={() => handleViewDetails(course.courseId)}
                    >
                      <FontAwesomeIcon icon={faEye} />
                      View Details
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Available Courses */}
      <div className="course-section">
        <div className="section-header">
          <h2>
            <FontAwesomeIcon icon={faUsers} className="section-icon" />
            Available Courses
            <span className="course-count">{availableCourses.length}</span>
          </h2>
        </div>
        <div className="courses-grid">
          {availableCourses.length === 0 ? (
            <div className="no-courses">
              <FontAwesomeIcon icon={faUsers} className="no-courses-icon" />
              <h3>No available courses</h3>
              <p>All courses are currently enrolled or completed.</p>
            </div>
          ) : (
            availableCourses.map((course) => {
              const coverImageData = getCourseCoverImage(course);

              return (
                <div key={course.courseId} className="course-card available">
                  <div className="course-card-image">
                    <img 
                      src={coverImageData.src}
                      alt={course.courseName}
                      className="course-cover-image"
                      data-placeholder={coverImageData.isPlaceholder}
                      onError={(e) => {
                        e.target.src = noImageAvailable;
                        e.target.setAttribute('data-placeholder', 'true');
                      }}
                    />
                  </div>

                  <div className="course-card-header">
                    <div className="course-code-version">
                      <span className="course-code">{course.courseCode}</span>
                      {course.courseVersion && (
                        <span className="course-version">v{course.courseVersion}</span>
                      )}
                    </div>
                  </div>

                  <div className="course-card-body">
                    <h3 className="course-title">{course.courseName}</h3>
                    <p className="course-description">
                      {course.courseDecription || "No description available"}
                    </p>

                    <div className="course-details">
                      {course.dlTrainingCourseModules && (
                        <div className="course-detail">
                          <FontAwesomeIcon icon={faBookOpen} />
                          <span>{course.dlTrainingCourseModules.length} Module{course.dlTrainingCourseModules.length !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="course-card-footer">
                    <button
                      className="btn-view-details"
                      onClick={() => handleViewDetails(course.courseId)}
                    >
                      <FontAwesomeIcon icon={faEye} />
                      View Details
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Completed Courses */}
      <div className="course-section">
        <div className="section-header expandable" onClick={() => setIsCompletedExpanded(!isCompletedExpanded)}>
          <h2>
            <FontAwesomeIcon icon={faCheck} className="section-icon" />
            Completed Courses
            <span className="course-count">{completedCourses.length}</span>
            <FontAwesomeIcon 
              icon={isCompletedExpanded ? faChevronUp : faChevronDown} 
              className="expand-icon" 
            />
          </h2>
        </div>
        {isCompletedExpanded && (
          <div className="courses-grid">
            {completedCourses.length === 0 ? (
              <div className="no-courses">
                <FontAwesomeIcon icon={faCheck} className="no-courses-icon" />
                <h3>No completed courses</h3>
                <p>Complete some courses to see them here.</p>
              </div>
            ) : (
              completedCourses.map((course) => {
                const enrollmentStatus = getEnrollmentStatus(course.courseId);
                const coverImageData = getCourseCoverImage(course);

                return (
                  <div key={course.courseId} className="course-card completed">
                    <div className="course-card-image">
                      <img 
                        src={coverImageData.src}
                        alt={course.courseName}
                        className="course-cover-image"
                        data-placeholder={coverImageData.isPlaceholder}
                        onError={(e) => {
                          e.target.src = noImageAvailable;
                          e.target.setAttribute('data-placeholder', 'true');
                        }}
                      />
                    </div>

                    <div className="course-card-header">
                      <div className="course-code-version">
                        <span className="course-code">{course.courseCode}</span>
                        {course.courseVersion && (
                          <span className="course-version">v{course.courseVersion}</span>
                        )}
                      </div>
                      {enrollmentStatus && (
                        <div className={`enrollment-badge ${enrollmentStatus.class}`}>
                          <FontAwesomeIcon icon={enrollmentStatus.icon} />
                          <span>{enrollmentStatus.text}</span>
                        </div>
                      )}
                    </div>

                    <div className="course-card-body">
                      <h3 className="course-title">{course.courseName}</h3>
                      <p className="course-description">
                        {course.courseDecription || "No description available"}
                      </p>

                      <div className="course-details">
                        <div className="course-detail">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>Completed: {formatCourseDate(getCourseEnrollment(course.courseId)?.courseCompleteByDate)}</span>
                        </div>
                        {course.dlTrainingCourseModules && (
                          <div className="course-detail">
                            <FontAwesomeIcon icon={faBookOpen} />
                            <span>{course.dlTrainingCourseModules.length} Module{course.dlTrainingCourseModules.length !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="course-card-footer">
                      <button
                        className="btn-view-details"
                        onClick={() => handleViewDetails(course.courseId)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingCourses;